import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { Elements } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import { generatePath, useHistory } from 'react-router-dom'
import Icon from '@mdi/react'
import { mdiClose } from '@mdi/js'

import { H2 } from 'components/Typography/Headings'
import { subscriptionUpdateMessages } from './messages'
import UpgradeSubscription from './UpdateSubscription'
import { useAppDispatch, useAppSelector } from 'app/hooks'
import routes from 'app/routes'
import theme from 'theme_'

import { useHomeRouteParams } from 'features/home/hooks'

import elementsLocaleMapper from 'app/utils/elementsLocaleMapper'
import { SpinnerCentered } from 'components/Loader'
import {
  getPaymentMethodsListActions,
  getPaymentMethodsListThunk,
} from 'features/paymentMethods/slice'
import SubsUpdateRightPanel from './SubsManageRightPanel'
import CheckoutDetailsBlock from '../SubscriptionManagement/CheckoutDetailsBlock'
import {
  defaultPaymentMethodRequestSelectors,
  paymentMethodsListSelector,
  hasNoPaymentMethodsSelector,
} from 'features/paymentMethods/selectors'
import AddPaymentMethodForm from '../SubscriptionManagement/AddPaymentMethodForm'
import { subscriptionManagementMessages } from '../SubscriptionManagement/messages'
import { STRIPE_PK } from 'app/consts'
import { LocalizationContext } from 'components/i18n/LocalizationWrapper/LocalizationWrapper'
import { WhiteBlock } from 'components/Layout'
import { StyledAddCardWrapper } from '../SubscriptionManagement/AddCard/styles'
import { setupIntentSelector } from '../selectors'
import { getHomeSubscriptonInfo } from 'features/home/utils'
import { selectedHomeSelector } from 'features/home/selectors'
import { createSetupIntentThunk } from '../slice'
import SubsManageBottomPanel from 'components/SubsManageBottomPanel'
import { currentRootDomainIdSelector } from 'features/rootDomains/selectors'
import {
  SubsUpdateContainer,
  SubsUpdateBody,
  SubsUpdateLeftPanelContainer,
  CloseBtn,
} from './styles'
import { subscriptionHomePriceActions } from '../SubscriptionDetails/slice'

function SubscriptionUpdate() {
  const dispatch = useAppDispatch()
  const { homeId } = useHomeRouteParams()
  const { formatMessage } = useIntl()
  const history = useHistory()

  const rootDomainId = useAppSelector(currentRootDomainIdSelector)
  const localizationContext = useContext(LocalizationContext)

  const home = useAppSelector(selectedHomeSelector.data)
  const subscriptionInfo = getHomeSubscriptonInfo(home)

  const paymentMethods = useAppSelector(paymentMethodsListSelector.data)
  const paymentMethodsLoaded = useAppSelector(
    paymentMethodsListSelector.isFinished
  )
  const isFetchingPaymentMethodsList = useAppSelector(
    paymentMethodsListSelector.isFetching
  )
  const isFetchingDefaultPaymentMethod = useAppSelector(
    defaultPaymentMethodRequestSelectors.isFetching
  )
  const isLoadingSetupIntent = useAppSelector(setupIntentSelector.isFetching)

  const [isAddingNewMethod, setIsAddingNewMethod] = useState(
    !paymentMethods?.length
  )

  const subscriptionEnabled = useMemo(() => {
    if (
      home &&
      home?.hostInfo?.hostCapabilities[0]?.version === 1 &&
      home?.hostInfo?.hostCapabilities[0]?.type === 'cloudLicenses'
    ) {
      return false
    }
    return true
  }, [home])

  useEffect(() => {
    if (subscriptionInfo?.autoRenew || !subscriptionEnabled) {
      if (rootDomainId) {
        const homeStatusPath = generatePath(routes.homeStatus, {
          homeId,
          rootDomainId,
        })
        history.push(homeStatusPath)
      }
    }
  }, [subscriptionInfo, subscriptionEnabled, history, homeId, rootDomainId])

  const loaded =
    paymentMethodsLoaded &&
    !isFetchingPaymentMethodsList &&
    !isFetchingDefaultPaymentMethod &&
    !isLoadingSetupIntent

  const stripePromise = useMemo(
    () =>
      loadStripe(STRIPE_PK || '', {
        locale: elementsLocaleMapper(localizationContext.locale),
      }),
    [localizationContext.locale]
  )

  const handlePaymentMethodAdd = () => {
    rootDomainId && dispatch(getPaymentMethodsListThunk(rootDomainId))
  }

  useEffect(() => {
    if (homeId && rootDomainId) {
      dispatch(getPaymentMethodsListThunk(rootDomainId))
    }

    return () => {
      dispatch(subscriptionHomePriceActions.clear())
      dispatch(getPaymentMethodsListActions.clear())
    }
  }, [dispatch, homeId, rootDomainId])

  useEffect(() => {
    if (paymentMethodsLoaded && rootDomainId) {
      dispatch(createSetupIntentThunk(rootDomainId))
    }
  }, [dispatch, paymentMethodsLoaded, rootDomainId])

  useEffect(() => {
    if (paymentMethods?.length) {
      setIsAddingNewMethod(false)
    } else {
      setIsAddingNewMethod(true)
    }
  }, [paymentMethods])

  const handleCancel = useCallback(() => {
    if (rootDomainId) {
      const homeStatusPath = generatePath(routes.homeStatus, {
        homeId,
        rootDomainId,
      })
      history.push(homeStatusPath)
    }
  }, [history, homeId, rootDomainId])

  const hasNoPaymentMethods = useAppSelector(hasNoPaymentMethodsSelector)

  const handleAddNewMethod = () => {
    setIsAddingNewMethod(true)
  }

  return (
    <SubsUpdateContainer>
      <H2>{formatMessage(subscriptionUpdateMessages.title)}</H2>

      {loaded ? (
        <>
          <SubsUpdateBody>
            <SubsUpdateLeftPanelContainer>
              <StyledAddCardWrapper>
                {isAddingNewMethod ? (
                  <WhiteBlock>
                    {!hasNoPaymentMethods && (
                      <CloseBtn onClick={() => setIsAddingNewMethod(false)}>
                        <Icon
                          path={mdiClose}
                          size={1}
                          color={theme.colors.horizonOrange}
                        />
                      </CloseBtn>
                    )}

                    <Elements
                      key={localizationContext.locale}
                      stripe={stripePromise}
                    >
                      <AddPaymentMethodForm
                        isFirstMethod={hasNoPaymentMethods}
                        onSuccess={handlePaymentMethodAdd}
                      >
                        {({
                          isSubmitDisabled,
                        }: {
                          isSubmitDisabled: boolean
                        }) => (
                          <>
                            <SubsManageBottomPanel
                              isSubmitDisabled={isSubmitDisabled}
                              totalToPayAmount="$0"
                              submitLabel={formatMessage(
                                subscriptionManagementMessages.addPaymentMethodSubmit
                              )}
                              handleSubmit={() => {}}
                              handleCancel={handleCancel}
                            />
                          </>
                        )}
                      </AddPaymentMethodForm>
                    </Elements>
                  </WhiteBlock>
                ) : (
                  <UpgradeSubscription onAddNewMethod={handleAddNewMethod} />
                )}
              </StyledAddCardWrapper>
            </SubsUpdateLeftPanelContainer>
            <SubsUpdateRightPanel />
            <CheckoutDetailsBlock />
          </SubsUpdateBody>
        </>
      ) : (
        <SpinnerCentered />
      )}
    </SubsUpdateContainer>
  )
}

export default SubscriptionUpdate
